import { knownPlainValues } from './vaultSession';

/**
 * Refuse to persist an object that carries a decrypted real name.
 *
 * ── WHY A VALUE CHECK AND NOT A KEY CHECK ───────────────────────────────────────────────────────
 *
 * This is the part that is easy to get wrong. The old mechanism put real names in `realFirstName` /
 * `realLastName`, so a guard could watch for those KEY names. The vault does not: the resolver returns a
 * real name that belongs in `firstName`, which is the same legitimate field a pseudonym occupies. A key
 * allowlist therefore sees a perfectly ordinary student and waves it through.
 *
 * So the guard compares VALUES against what is currently decrypted for that class. It can only do this
 * because the vault is unlocked in the same session — which is exactly when the mistake is possible. When
 * the vault is locked there is nothing to leak and nothing to check.
 *
 * ── WHAT IT IS FOR ──────────────────────────────────────────────────────────────────────────────
 *
 * The codebase persists whole objects in places that do not look like writes: `handleDeleteClass`
 * snapshots the entire `ClassData` into `deletionHistory`, `ClassSettingsModal` saves the whole roster
 * through `updateClass`, `createClass` re-splits an embedded roster on restore. Any of them, handed a
 * student that someone "helpfully" merged a resolved name onto, writes that name to Firestore — and
 * nothing downstream notices. This is the `maskNames` write-guard pattern applied to the one thing that
 * pattern cannot see by key.
 *
 * Throws rather than stripping. A caller that believes it saved a name and did not is a worse outcome
 * than a visible failure, and a silent strip is how a half-working privacy feature survives review.
 */

/** Fields never worth scanning: ids and timestamps cannot collide with a name meaningfully. */
const IGNORED_KEYS = new Set([
  'id', 'studentId', 'classId', 'createdAt', 'updatedAt', 'timestamp',
  'xp', 'level', 'rev', 'v', 'iv', 'ct',
]);

/**
 * Walk an object for any string value that matches a known decrypted value for this class.
 *
 * Recursive, because the thing being persisted may be a whole class with a `students` array, each of
 * which may carry a merged name. A non-recursive check was one of the ways an earlier guard in this
 * codebase passed while the data walked out underneath it.
 */
export function findLeakedVaultValues(
  value: unknown,
  uid: string,
  classId: string,
  opts: { maxDepth?: number; maxNodes?: number } = {},
): string[] {
  const known = knownPlainValues(uid, classId);
  if (!known.size) return [];
  const hits: string[] = [];
  /**
   * Depth 32, not 8, and bounded by NODE COUNT instead.
   *
   * 8 was a guess, and it broke the largest objects the guard exists for. A class document reaches depth 9 at
   * `popQuizzes[i].questions[j].variants[k].options[l]`, and kinder results nest comparably — so with the
   * fail-closed behaviour above, saving a match-type test or deleting a class threw `VaultLeakError` for a
   * perfectly clean document, the moment its vault was unlocked. Failing closed was right; the limit was
   * wrong, and the combination was worse than either.
   *
   * A node cap is the honest bound: it protects against a pathological structure without caring how deep a
   * legitimate one happens to be. The walk is a Set lookup per string, and the fan-out is already bounded by
   * Firestore's document size limit.
   */
  const maxDepth = opts.maxDepth ?? 32;
  const maxNodes = opts.maxNodes ?? 200_000;
  let nodes = 0;

  /** Set when the walk gave up, so the caller can fail closed rather than read silence as cleanliness. */
  let truncated = false;

  const walk = (v: unknown, path: string, depth: number): void => {
    if (depth > maxDepth) {
      /**
       * FAIL CLOSED. Returning here used to look like "nothing found", so an object nested deeper than the
       * limit passed the guard by being complicated. The walk is cheap; the right answer to "I could not
       * finish looking" is never "it is clean".
       */
      truncated = true;
      return;
    }
    nodes += 1;
    if (nodes > maxNodes) { truncated = true; return; }
    if (hits.length > 20) { truncated = true; return; }
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (s.length > 2 && known.has(s)) hits.push(path);
      return;
    }
    if (Array.isArray(v)) {
      v.forEach((item, i) => walk(item, `${path}[${i}]`, depth + 1));
      return;
    }
    if (v && typeof v === 'object') {
      for (const [k, item] of Object.entries(v as Record<string, unknown>)) {
        if (IGNORED_KEYS.has(k)) continue;
        walk(item, path ? `${path}.${k}` : k, depth + 1);
      }
    }
  };

  walk(value, '', 0);
  if (truncated && !hits.length) {
    /**
     * Report the truncation as a finding of its own, so `assertNoVaultLeak` throws instead of passing — and
     * name the cap that ACTUALLY fired. It always said "nesting deeper than N", which sent the reader looking
     * for deep structure when the real cause was usually breadth: a large grade map trips the node cap at a
     * depth of three.
     */
    hits.push(nodes > maxNodes
      ? `<unscannable: more than ${maxNodes} values>`
      : `<unscannable: nesting deeper than ${maxDepth}>`);
  }
  return hits;
}

/** Throw if this object carries a decrypted value that must not be persisted. */
export function assertNoVaultLeak(
  value: unknown,
  uid: string,
  classId: string,
  what: string,
): void {
  const hits = findLeakedVaultValues(value, uid, classId);
  if (!hits.length) return;
  throw new VaultLeakError(what, hits);
}

export class VaultLeakError extends Error {
  constructor(public readonly what: string, public readonly paths: string[]) {
    super(
      `Refusing to persist ${what}: it carries student information that is only supposed to exist inside `
      + `the vault (${paths.slice(0, 5).join(', ')}${paths.length > 5 ? `, +${paths.length - 5} more` : ''}). `
      + 'Read names through resolveDisplayName at render time instead of merging them onto the object.',
    );
    this.name = 'VaultLeakError';
  }
}
